import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  signal,
  HostListener,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RegsApiService } from '../../data/regs-api.service';
import { FollowPrefs, FollowResult, RegsFollowService } from '../../data/regs-follow.service';
import { Sector, Species } from '../../domain/regs-models';

@Component({
  selector: 'app-keep-it-follow-sheet',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="follow-modal-backdrop" (click)="onCloseClick()">
      <div class="follow-modal-sheet" (click)="$event.stopPropagation()">
        <div class="modal-grabber"></div>

        <div class="modal-header">
          <div class="skipper-avatar-circle">
            <img src="/assets/common_icons/vectormarine.svg" alt="Skipper" />
          </div>
          <div class="modal-header-text">
            <h2 class="modal-title">Follow {{ species.short.toLowerCase() }}</h2>
            <p class="modal-sub">{{ sectorLabel }} · all waters</p>
          </div>
          <button type="button" class="icon-circle-btn" (click)="onCloseClick()" aria-label="Close">
            <span class="material-icons">close</span>
          </button>
        </div>

        <div class="modal-body">
          <div class="skipper-says-row">
            <div class="skipper-bubble">
              I’ll keep watch and tell you the moment something changes. What should I watch for?
            </div>
          </div>

          <div class="section-label">Tell me about</div>
          <div class="settings-card">
            <div class="switch-row">
              <div class="switch-text">
                <span class="switch-title">Opens, closes and limits</span>
                <span class="switch-desc">Seasons, bag and size, category status</span>
              </div>
              <label class="toggle-switch">
                <input type="checkbox" [checked]="watchRules()" (change)="toggleKind('rules', $event)" />
                <span class="slider"></span>
              </label>
            </div>

            @if (hasQuota()) {
              <div class="switch-row">
                <div class="switch-text">
                  <span class="switch-title">Quota updates</span>
                  <span class="switch-desc">Each time NOAA posts new landings</span>
                </div>
                <label class="toggle-switch">
                  <input type="checkbox" [checked]="watchQuota()" (change)="toggleKind('quota', $event)" />
                  <span class="slider"></span>
                </label>
              </div>
            }

            <div class="switch-row">
              <div class="switch-text">
                <span class="switch-title">Day-before heads-ups</span>
                <span class="switch-desc">Like “no retention tomorrow”</span>
              </div>
              <label class="toggle-switch">
                <input type="checkbox" [checked]="watchHeadsup()" (change)="toggleKind('headsup', $event)" />
                <span class="slider"></span>
              </label>
            </div>
          </div>

          <div class="section-label" style="margin-top: 16px;">How Skipper tells you</div>
          <div class="settings-card">
            <div class="switch-row">
              <div class="switch-text">
                <span class="switch-title">In-app alert notification</span>
                <span class="switch-desc">When you open the app or receive alerts</span>
              </div>
              <label class="toggle-switch">
                <input type="checkbox" [(ngModel)]="push" />
                <span class="slider"></span>
              </label>
            </div>

            <div class="switch-row">
              <div class="switch-text">
                <span class="switch-title">Pop up on the map</span>
                <span class="switch-desc">When the app is open</span>
              </div>
              <label class="toggle-switch">
                <input type="checkbox" [(ngModel)]="popup" />
                <span class="slider"></span>
              </label>
            </div>
          </div>

          <div class="actions-box">
            <button
              type="button"
              class="primary-btn"
              [disabled]="saving() || kinds().size === 0 || (!push && !popup)"
              (click)="save()"
            >
              {{ existing ? 'Save' : 'Follow' }}
            </button>

            @if (existing) {
              <button type="button" class="danger-btn" (click)="unfollow()">
                Stop following {{ species.short.toLowerCase() }}
              </button>
            } @else {
              <p class="footer-hint">You can change this anytime in Following.</p>
            }
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .follow-modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.4);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10001;
        animation: fadeIn 200ms ease;
      }
      .follow-modal-sheet {
        width: calc(100% - 32px);
        max-width: 400px;
        background: #ffffff;
        border-radius: 20px;
        max-height: 85vh;
        overflow-y: auto;
        padding-bottom: 24px;
        box-shadow: 0 16px 40px rgba(0, 0, 0, 0.22);
        animation: scaleIn 220ms cubic-bezier(0.16, 1, 0.3, 1);
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes scaleIn {
        from { opacity: 0; transform: scale(0.95); }
        to { opacity: 1; transform: scale(1); }
      }
      .modal-grabber {
        display: none;
      }
      .modal-header {
        display: flex;
        align-items: center;
        padding: 12px 16px;
      }
      .skipper-avatar-circle {
        width: 38px;
        height: 38px;
        border-radius: 50%;
        background: #eaf2fe;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: none;
      }
      .skipper-avatar-circle img {
        width: 24px;
        height: 24px;
      }
      .modal-header-text {
        flex: 1;
        margin: 0 12px;
      }
      .modal-title {
        font-size: 17px;
        font-weight: 600;
        color: #1c1c1e;
        margin: 0;
      }
      .modal-sub {
        font-size: 13px;
        color: #8a8a8e;
        margin: 2px 0 0;
      }
      .icon-circle-btn {
        width: 34px;
        height: 34px;
        border-radius: 50%;
        border: none;
        background: #f2f2f6;
        color: #3a3a3c;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }
      .icon-circle-btn .material-icons {
        font-size: 20px;
      }
      .modal-body {
        padding: 8px 16px;
      }
      .skipper-says-row {
        margin-bottom: 16px;
      }
      .skipper-bubble {
        padding: 12px 15px;
        background: #eef5fe;
        border: 1px solid #d9e7fc;
        border-radius: 18px;
        font-size: 15px;
        color: #2c2c2e;
        line-height: 1.38;
      }
      .section-label {
        font-size: 12px;
        font-weight: 600;
        color: #8a8a8e;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 8px;
        padding-left: 4px;
      }
      .settings-card {
        background: #ffffff;
        border: 1px solid #e5e5ea;
        border-radius: 16px;
        overflow: hidden;
      }
      .switch-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 14px;
        border-bottom: 1px solid #ededf0;
      }
      .switch-row:last-child {
        border-bottom: none;
      }
      .switch-text {
        display: flex;
        flex-direction: column;
        padding-right: 12px;
      }
      .switch-title {
        font-size: 16px;
        font-weight: 600;
        color: #1c1c1e;
      }
      .switch-desc {
        font-size: 13px;
        color: #8a8a8e;
        margin-top: 2px;
      }
      .toggle-switch {
        position: relative;
        display: inline-block;
        width: 50px;
        height: 30px;
        flex: none;
      }
      .toggle-switch input {
        opacity: 0;
        width: 0;
        height: 0;
      }
      .slider {
        position: absolute;
        cursor: pointer;
        inset: 0;
        background-color: #e5e5ea;
        transition: 0.3s;
        border-radius: 30px;
      }
      .slider:before {
        position: absolute;
        content: '';
        height: 26px;
        width: 26px;
        left: 2px;
        bottom: 2px;
        background-color: white;
        transition: 0.3s;
        border-radius: 50%;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
      }
      input:checked + .slider {
        background-color: #34c759;
      }
      input:checked + .slider:before {
        transform: translateX(20px);
      }
      .actions-box {
        margin-top: 20px;
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .primary-btn {
        width: 100%;
        height: 52px;
        border-radius: 14px;
        border: none;
        background: #2463eb;
        color: #ffffff;
        font-size: 17px;
        font-weight: 600;
        cursor: pointer;
        transition: opacity 120ms ease;
      }
      .primary-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      .danger-btn {
        width: 100%;
        padding: 10px;
        border: none;
        background: transparent;
        color: #c3283a;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
      }
      .footer-hint {
        text-align: center;
        font-size: 13px;
        color: #8a8a8e;
        margin: 4px 0 0;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeepItFollowSheetComponent implements OnInit {
  private followService = inject(RegsFollowService);
  private api = inject(RegsApiService);

  @Input({ required: true }) species!: Species;
  @Input({ required: true }) sector!: Sector;

  @Output() completed = new EventEmitter<FollowResult>();
  @Output() close = new EventEmitter<void>();
  @Output() closed = new EventEmitter<FollowResult | 'cancelled'>();

  existing?: FollowPrefs;
  readonly hasQuota = signal(false);
  readonly kinds = signal<Set<string>>(new Set(['rules', 'headsup']));
  readonly saving = signal(false);

  push = true;
  popup = true;

  get sectorLabel(): string {
    return this.sector === 'recreational' ? 'Recreational' : 'Commercial';
  }

  ngOnInit(): void {
    this.existing = this.followService.prefsFor(this.species.id, this.sector);
    if (this.existing) {
      this.kinds.set(new Set(this.existing.kinds));
      this.push = this.existing.push;
      this.popup = this.existing.popup;
    } else {
      this.kinds.set(new Set(['rules', 'headsup']));
    }

    if (this.species.hasLive(this.sector)) {
      this.api
        .live(this.species.id, this.sector)
        .then((d) => {
          if (d.hasQuotaNumbers) {
            this.hasQuota.set(true);
            if (!this.existing) {
              this.kinds.update((k) => new Set([...k, 'quota']));
            }
          }
        })
        .catch(() => {});
    }
  }

  watchRules(): boolean {
    return this.kinds().has('rules');
  }

  watchQuota(): boolean {
    return this.kinds().has('quota');
  }

  watchHeadsup(): boolean {
    return this.kinds().has('headsup');
  }

  toggleKind(kind: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.kinds.update((set) => {
      const next = new Set(set);
      if (checked) next.add(kind);
      else next.delete(kind);
      return next;
    });
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscape(event: KeyboardEvent): void {
    event.preventDefault();
    this.onCloseClick();
  }

  onCloseClick(): void {
    this.close.emit();
    this.closed.emit('cancelled');
  }

  async save(): Promise<void> {
    this.saving.set(true);
    try {
      const activeKinds = Array.from(this.kinds()).filter((k) => k !== 'quota' || this.hasQuota());
      const result = await this.followService.follow(this.species.id, this.sector, {
        kinds: activeKinds,
        push: this.push,
        popup: this.popup,
        since: new Date().toISOString(),
      });
      this.completed.emit(result);
      this.closed.emit(result);
    } finally {
      this.saving.set(false);
    }
  }

  async unfollow(): Promise<void> {
    await this.followService.unfollow(this.species.id, this.sector);
    this.completed.emit('unfollowed');
    this.closed.emit('unfollowed');
  }
}
